import { ZuruflexApiError } from '@zuruflex/api-client';
import { formatDateIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import type { PromotionRedemptionRow } from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorState,
  Money,
  PageHeader,
  Skeleton,
  StatTile,
  type ColumnDef,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Coins, Receipt, Tag, TrendingUp } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PromotionDialog } from '../components/PromotionDialog.js';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { PHASE_VARIANT, promoPhase, promoValue, promoWindow } from '../lib/promotions.js';
import { useTableLabels } from '../lib/table-labels.js';

const REDEMPTION_VARIANT = {
  RESERVED: 'warning',
  CONFIRMED: 'success',
  RELEASED: 'outline',
} as const;

/**
 * One promo: what it has cost this partner, what it brought in, and every
 * hire that used it.
 *
 * The cost figure is the one a partner opens this for. They pay for their
 * own promos, so "what it has cost you" is their money, not a marketing
 * metric — and it sits beside what the same hires brought in, because a
 * cost with nothing to weigh it against reads as a loss.
 */
export function PromotionDetailPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { id = '' } = useParams<{ id: string }>();
  const { user, partner } = usePartner();
  const canManage = user.role === 'OWNER' || user.role === 'MANAGER';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tableLabels = useTableLabels();
  const [editing, setEditing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    data: promo,
    isPending,
    error: loadError,
    refetch,
  } = useQuery({ queryKey: ['promotion', id], queryFn: () => api.getPromotion(id) });

  const redemptions = useQuery({
    queryKey: ['promotion', id, 'redemptions'],
    queryFn: () => api.promotionRedemptions(id, { perPage: 100 }),
  });

  const { data: fleet } = useQuery({
    queryKey: ['my-fleet'],
    queryFn: () => api.listVehicles({ partnerId: partner.id, perPage: 100 }),
    enabled: Boolean(promo && promo.vehicleIds.length > 0),
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['promotion', id] }),
      queryClient.invalidateQueries({ queryKey: ['my-promotions'] }),
    ]);
  };

  const setStatus = useMutation({
    mutationFn: (to: 'pause' | 'resume' | 'archive') =>
      to === 'pause'
        ? api.pausePromotion(id)
        : to === 'resume'
          ? api.resumePromotion(id)
          : api.archivePromotion(id),
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async () => {
      setArchiving(false);
      await refresh();
    },
  });

  const columns = useMemo<ColumnDef<PromotionRedemptionRow>[]>(
    () => [
      {
        id: 'booking',
        header: t('promotions.redemption.booking'),
        cell: ({ row }) => (
          <span className="font-mono text-sm text-text">{row.original.booking.reference}</span>
        ),
      },
      {
        id: 'guest',
        header: t('promotions.redemption.guest'),
        cell: ({ row }) => row.original.customer.fullName ?? t('bookings.unnamedCustomer'),
      },
      {
        id: 'dates',
        header: t('promotions.redemption.dates'),
        cell: ({ row }) => (
          <span className="text-sm text-text-muted">
            {formatDateIn(row.original.booking.pickupAt, locale)} →{' '}
            {formatDateIn(row.original.booking.dropoffAt, locale)}
          </span>
        ),
      },
      {
        id: 'discount',
        header: t('promotions.redemption.discount'),
        cell: ({ row }) => (
          <Money amount={row.original.discountAmount} currency={row.original.currency} />
        ),
      },
      {
        id: 'status',
        header: t('promotions.redemption.status'),
        cell: ({ row }) => (
          <Badge variant={REDEMPTION_VARIANT[row.original.status]}>
            {t(`promotions.redemption.${row.original.status}`)}
          </Badge>
        ),
      },
    ],
    [t, locale],
  );

  const back = (
    <Link
      to="/promotions"
      className="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
    >
      <ChevronLeft className="size-4" />
      {t('promotions.detail.back')}
    </Link>
  );

  if (loadError) {
    return (
      <>
        <PageHeader title={t('promotions.title')} eyebrow={back} />
        <ErrorState error={loadError} onRetry={() => void refetch()} />
      </>
    );
  }

  if (isPending || !promo) {
    return (
      <>
        <PageHeader title={t('common:state.loading')} eyebrow={back} />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((tile) => (
            <Skeleton key={tile} className="h-28" />
          ))}
        </div>
      </>
    );
  }

  const phase = promoPhase(promo);
  const money = (amount: number) => formatMoneyIn(amount, promo.currency, locale);
  const carNames = new Map(
    (fleet?.items ?? []).map((car) => [car.id, `${car.make} ${car.model} · ${car.plateNumber}`]),
  );

  /*
   * The terms as sentences rather than a form read back: what a renter has
   * to do to get it, in the order they would meet each condition.
   */
  const terms: { label: string; value: string }[] = [
    {
      label: t('promotions.col.value'),
      value:
        promoValue(promo, t, locale) +
        (promo.discountType === 'PERCENT' && promo.maxDiscount
          ? `, ${t('promotions.upTo', { amount: money(promo.maxDiscount) })}`
          : ''),
    },
    { label: t('promotions.col.code'), value: promo.code ?? t('promotions.automatic') },
    {
      label: t('promotions.form.audience'),
      value: t(
        `promotions.form.audience${promo.audience === 'FIRST_HIRE' ? 'FIRST_HIRE' : 'EVERYONE'}`,
      ),
    },
    { label: t('promotions.col.runs'), value: promoWindow(promo, t, locale) },
    {
      label: t('promotions.col.uses'),
      value: [
        promo.maxRedemptions
          ? t('promotions.usesOf', { count: promo.redemptionCount, max: promo.maxRedemptions })
          : t('promotions.unlimited'),
        t('promotions.perCustomer', { count: promo.maxPerCustomer }),
      ].join(' · '),
    },
    ...(promo.minDays
      ? [
          {
            label: t('promotions.form.minDays'),
            value: t('promotions.minDays', { count: promo.minDays }),
          },
        ]
      : []),
    ...(promo.minSubtotal
      ? [
          {
            label: t('promotions.form.minSubtotal', { currency: promo.currency }),
            value: t('promotions.minSubtotal', { amount: money(promo.minSubtotal) }),
          },
        ]
      : []),
    {
      label: t('promotions.form.cars'),
      value:
        promo.vehicleIds.length === 0
          ? t('promotions.allCars')
          : promo.vehicleIds.map((carId) => carNames.get(carId) ?? '…').join(', '),
    },
  ];

  const archived = promo.status === 'ARCHIVED';

  return (
    <>
      <PageHeader
        eyebrow={back}
        title={promo.name}
        description={
          <span className="inline-flex flex-wrap items-center gap-2">
            <Badge variant={PHASE_VARIANT[phase]}>{t(`promotions.status.${phase}`)}</Badge>
            {promo.code ? (
              <span className="font-mono">{promo.code}</span>
            ) : (
              t('promotions.automatic')
            )}
            <span>· {promoValue(promo, t, locale)}</span>
          </span>
        }
        action={
          canManage && !archived ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditing(true)}>
                {t('promotions.detail.edit')}
              </Button>
              <Button
                variant="outline"
                isLoading={setStatus.isPending && setStatus.variables !== 'archive'}
                onClick={() => setStatus.mutate(promo.status === 'PAUSED' ? 'resume' : 'pause')}
              >
                {promo.status === 'PAUSED'
                  ? t('promotions.detail.resume')
                  : t('promotions.detail.pause')}
              </Button>
              <Button variant="ghost" onClick={() => setArchiving(true)}>
                {t('promotions.detail.archive')}
              </Button>
            </div>
          ) : null
        }
      />

      {error && !archiving ? (
        <Alert variant="danger" className="mb-6">
          {error}
        </Alert>
      ) : null}
      {archived ? (
        <Alert variant="info" className="mb-6">
          {t('promotions.detail.archived')}
        </Alert>
      ) : null}

      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile
            label={t('promotions.stats.redemptions')}
            value={String(promo.stats.redemptions)}
            hint={t('promotions.stats.redemptionsHint', {
              reserved: promo.stats.reserved,
              confirmed: promo.stats.confirmed,
            })}
            icon={<Tag className="size-4" />}
          />
          <StatTile
            label={t('promotions.stats.discount')}
            value={money(promo.stats.totalDiscount)}
            hint={t('promotions.stats.discountHint')}
            icon={<Coins className="size-4" />}
            accent
          />
          <StatTile
            label={t('promotions.stats.bookings')}
            value={String(promo.stats.bookings)}
            hint={t('promotions.stats.bookingsHint', { released: promo.stats.released })}
            icon={<Receipt className="size-4" />}
          />
          <StatTile
            label={t('promotions.stats.revenue')}
            value={money(promo.stats.revenue)}
            hint={t('promotions.stats.revenueHint')}
            icon={<TrendingUp className="size-4" />}
          />
        </div>

        <section className="rounded-card border border-border-default bg-surface-raised p-5">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-text-muted">
            {t('promotions.detail.terms')}
          </h2>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {terms.map((term) => (
              <div key={term.label} className="min-w-0">
                <dt className="text-xs text-text-muted">{term.label}</dt>
                <dd className="text-sm text-text">{term.value}</dd>
              </div>
            ))}
          </dl>
          {promo.description ? (
            <p className="mt-4 text-sm text-text-muted">{promo.description}</p>
          ) : null}
        </section>

        <section>
          <h2 className="mb-3 text-lg font-semibold text-text">
            {t('promotions.detail.redemptions')}
          </h2>
          {redemptions.error ? (
            <ErrorState error={redemptions.error} onRetry={() => void redemptions.refetch()} />
          ) : (
            <DataTable
              labels={tableLabels}
              columns={columns}
              data={redemptions.data?.items ?? []}
              isLoading={redemptions.isPending}
              onRowClick={(row) =>
                navigate(`/bookings?ref=${encodeURIComponent(row.booking.reference)}`)
              }
              emptyMessage={t('promotions.detail.noRedemptions')}
            />
          )}
        </section>
      </div>

      {editing ? (
        <PromotionDialog
          open
          promotion={promo}
          onOpenChange={(next) => {
            if (!next) setEditing(false);
          }}
        />
      ) : null}

      <Dialog open={archiving} onOpenChange={setArchiving}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('promotions.detail.archiveTitle', { name: promo.name })}</DialogTitle>
            <DialogDescription>{t('promotions.detail.archiveBody')}</DialogDescription>
          </DialogHeader>
          {error ? <Alert variant="danger">{error}</Alert> : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiving(false)}>
              {t('common:action.cancel')}
            </Button>
            <Button
              variant="destructive"
              isLoading={setStatus.isPending}
              onClick={() => setStatus.mutate('archive')}
            >
              {t('promotions.detail.archiveConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
